/**
 * ToneGenerator.jsx — Administrative Audio Lab & Tone Synthesizer Widget.
 *
 * Provides real-time precision tone generation, multi-waveform LFO modulation,
 * additive harmonics, filter sweeps, DTMF dialing, binaural beats, and live oscilloscope.
 */
import { createSignal, createMemo, createEffect, onMount, onCleanup, For, Show } from 'solid-js';
import useWidgetData from './useWidgetData.js';
import toast from '../lib/toastStore.js';
import './ToneGenerator.css';

// Musical note computation
const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
function freqToNote(freq) {
  if (!freq || freq < 16) return '—';
  const n = 12 * (Math.log2(freq / 440)) + 69;
  const rounded = Math.round(n);
  const name = NOTE_NAMES[((rounded % 12) + 12) % 12];
  const octave = Math.floor(rounded / 12) - 1;
  const cents = Math.round((n - rounded) * 100);
  const centsStr = cents === 0 ? '' : cents > 0 ? ` +${cents}¢` : ` ${cents}¢`;
  return `${name}${octave}${centsStr}`;
}

// Logarithmic slider helpers (20Hz to 20000Hz)
const MIN_FREQ = 20;
const MAX_FREQ = 20000;
function freqToSlider(f) {
  const clamped = Math.max(MIN_FREQ, Math.min(MAX_FREQ, f));
  return (Math.log10(clamped / MIN_FREQ) / Math.log10(MAX_FREQ / MIN_FREQ)) * 1000;
}
function sliderToFreq(s) {
  const f = MIN_FREQ * Math.pow(MAX_FREQ / MIN_FREQ, s / 1000);
  return Math.round(f * 10) / 10;
}

export default function ToneGenerator(props) {
  const { data, loading, error, refetch } = useWidgetData('tone-generator', () => ({}));

  // Audio Context and Nodes state
  let audioCtx = null;
  let masterGain = null;
  let limiter = null;
  let analyser = null;
  let pannerNode = null;
  let filterNode = null;
  let primaryOsc = null;
  let harm1GainNode = null;
  let harm2Osc = null;
  let harm2GainNode = null;
  let harm3Osc = null;
  let harm3GainNode = null;
  let harm4Osc = null;
  let harm4GainNode = null;
  let lfoOsc = null;
  let lfoGain = null;
  let canvasRef = null;
  let animId = null;

  // Binaural nodes
  let binLeftOsc = null;
  let binRightOsc = null;
  let binLeftPan = null;
  let binRightPan = null;

  // DTMF active nodes
  let dtmfRowOsc = null;
  let dtmfColOsc = null;
  let dtmfGain = null;

  // Reactive UI Signals
  const initFreq = Number(props.settings?.frequency) || 440;
  const [isPlaying, setIsPlaying] = createSignal(false);
  const [frequency, setFrequency] = createSignal(initFreq);
  const [sliderVal, setSliderVal] = createSignal(freqToSlider(initFreq));
  const [volume, setVolume] = createSignal(Number(props.settings?.volume) || 25); // 0 - 100%
  const [isMuted, setIsMuted] = createSignal(false);
  const [waveform, setWaveform] = createSignal(props.settings?.waveform || 'sine'); // sine, triangle, square, sawtooth, white-noise, pink-noise
  const [activeTab, setActiveTab] = createSignal(props.settings?.activeTab || 'presets'); // presets, modulation, harmonics, filter, binaural, dtmf

  // Visualizer Mode
  const [vizMode, setVizMode] = createSignal(props.settings?.vizMode || 'oscilloscope'); // oscilloscope, spectrum

  // LFO Modulation Signals
  const [lfoEnabled, setLfoEnabled] = createSignal(false);
  const [lfoTarget, setLfoTarget] = createSignal('fm'); // fm, am, filter
  const [lfoWave, setLfoWave] = createSignal('sine');
  const [lfoRate, setLfoRate] = createSignal(5.0); // 0.1 - 40 Hz
  const [lfoDepth, setLfoDepth] = createSignal(50); // 0 - 100%

  // Harmonics Signals (Additive mixer)
  const [harm1Gain, setHarm1Gain] = createSignal(100); // 1x fundamental
  const [harm2Gain, setHarm2Gain] = createSignal(0);   // 2x octave
  const [harm3Gain, setHarm3Gain] = createSignal(0);   // 3x 5th
  const [harm4Gain, setHarm4Gain] = createSignal(0);   // 4x 2 octaves

  // Filter Signals
  const [filterEnabled, setFilterEnabled] = createSignal(false);
  const [filterType, setFilterType] = createSignal('lowpass'); // lowpass, highpass, bandpass, notch
  const [filterCutoff, setFilterCutoff] = createSignal(2500); // 20 - 20000 Hz
  const [filterQ, setFilterQ] = createSignal(1.5); // 0.1 - 20

  // Binaural Beat Signals
  const [binauralActive, setBinauralActive] = createSignal(false);
  const [binauralBaseHz, setBinauralBaseHz] = createSignal(216);
  const [binauralBeatHz, setBinauralBeatHz] = createSignal(10); // Alpha 10Hz

  // Sweep Signals
  const [sweepActive, setSweepActive] = createSignal(false);
  const [sweepStartHz, setSweepStartHz] = createSignal(100);
  const [sweepEndHz, setSweepEndHz] = createSignal(3000);
  const [sweepDurationSec, setSweepDurationSec] = createSignal(4);

  // Initialize Web Audio Context
  function initAudio() {
    if (audioCtx) return;
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    audioCtx = new AudioContext();

    // Hardware-style safety limiter (prevents harsh ear/speaker clipping)
    limiter = audioCtx.createDynamicsCompressor();
    limiter.threshold.setValueAtTime(-6.0, audioCtx.currentTime);
    limiter.knee.setValueAtTime(6.0, audioCtx.currentTime);
    limiter.ratio.setValueAtTime(16.0, audioCtx.currentTime);
    limiter.attack.setValueAtTime(0.003, audioCtx.currentTime);
    limiter.release.setValueAtTime(0.20, audioCtx.currentTime);

    // Master Gain
    masterGain = audioCtx.createGain();
    const targetVol = isMuted() ? 0 : (volume() / 100) * 0.45;
    masterGain.gain.setValueAtTime(targetVol, audioCtx.currentTime);

    // Stereo Panner
    pannerNode = audioCtx.createStereoPanner();

    // Biquad Filter
    filterNode = audioCtx.createBiquadFilter();
    filterNode.type = filterType();
    filterNode.frequency.setValueAtTime(filterCutoff(), audioCtx.currentTime);
    filterNode.Q.setValueAtTime(filterQ(), audioCtx.currentTime);

    // Analyser Node for Visualizer
    analyser = audioCtx.createAnalyser();
    analyser.fftSize = 1024;
    analyser.smoothingTimeConstant = 0.82;

    // Connect Main Chain: Filter -> Panner -> MasterGain -> Limiter -> Analyser -> Destination
    filterNode.connect(pannerNode);
    pannerNode.connect(masterGain);
    masterGain.connect(limiter);
    limiter.connect(analyser);
    analyser.connect(audioCtx.destination);
  }

  // Create Noise Buffer
  function createNoiseBuffer(type) {
    if (!audioCtx) return null;
    const bufferSize = audioCtx.sampleRate * 2;
    const buffer = audioCtx.createBuffer(1, bufferSize, audioCtx.sampleRate);
    const data = buffer.getChannelData(0);

    if (type === 'white-noise') {
      for (let i = 0; i < bufferSize; i++) {
        data[i] = Math.random() * 2 - 1;
      }
    } else {
      // Pink noise filter algorithm (Paul Kellet)
      let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
      for (let i = 0; i < bufferSize; i++) {
        const white = Math.random() * 2 - 1;
        b0 = 0.99886 * b0 + white * 0.0555179;
        b1 = 0.99332 * b1 + white * 0.0750759;
        b2 = 0.96900 * b2 + white * 0.1538520;
        b3 = 0.86650 * b3 + white * 0.3104856;
        b4 = 0.55000 * b4 + white * 0.5329522;
        b5 = -0.7616 * b5 - white * 0.0168980;
        data[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362) * 0.11;
        b6 = white * 0.115926;
      }
    }
    return buffer;
  }

  // Start sound generation
  function startSound() {
    initAudio();
    if (audioCtx.state === 'suspended') {
      audioCtx.resume();
    }

    stopActiveSoundNodes();

    const now = audioCtx.currentTime;
    const f = frequency();
    const isNoise = waveform().includes('noise');
    const destNode = filterEnabled() ? filterNode : pannerNode;

    if (isNoise) {
      const buffer = createNoiseBuffer(waveform());
      const noiseSource = audioCtx.createBufferSource();
      noiseSource.buffer = buffer;
      noiseSource.loop = true;
      noiseSource.connect(destNode);
      noiseSource.start(now);
      primaryOsc = noiseSource;
    } else {
      // Primary Oscillator
      primaryOsc = audioCtx.createOscillator();
      primaryOsc.type = waveform();
      primaryOsc.frequency.setValueAtTime(f, now);

      harm1GainNode = audioCtx.createGain();
      harm1GainNode.gain.setValueAtTime(harm1Gain() / 100, now);
      primaryOsc.connect(harm1GainNode);
      harm1GainNode.connect(destNode);
      primaryOsc.start(now);

      // Additive Harmonics (2x, 3x, 4x)
      harm2Osc = audioCtx.createOscillator();
      harm2Osc.type = waveform();
      harm2Osc.frequency.setValueAtTime(f * 2, now);
      harm2GainNode = audioCtx.createGain();
      harm2GainNode.gain.setValueAtTime(harm2Gain() / 100, now);
      harm2Osc.connect(harm2GainNode);
      harm2GainNode.connect(destNode);
      harm2Osc.start(now);

      harm3Osc = audioCtx.createOscillator();
      harm3Osc.type = waveform();
      harm3Osc.frequency.setValueAtTime(f * 3, now);
      harm3GainNode = audioCtx.createGain();
      harm3GainNode.gain.setValueAtTime(harm3Gain() / 100, now);
      harm3Osc.connect(harm3GainNode);
      harm3GainNode.connect(destNode);
      harm3Osc.start(now);

      harm4Osc = audioCtx.createOscillator();
      harm4Osc.type = waveform();
      harm4Osc.frequency.setValueAtTime(f * 4, now);
      harm4GainNode = audioCtx.createGain();
      harm4GainNode.gain.setValueAtTime(harm4Gain() / 100, now);
      harm4Osc.connect(harm4GainNode);
      harm4GainNode.connect(destNode);
      harm4Osc.start(now);

      // LFO Setup
      if (lfoEnabled()) {
        lfoOsc = audioCtx.createOscillator();
        lfoGain = audioCtx.createGain();
        lfoOsc.type = lfoWave();
        lfoOsc.frequency.setValueAtTime(lfoRate(), now);

        if (lfoTarget() === 'fm') {
          const fmDepthHz = (lfoDepth() / 100) * 500;
          lfoGain.gain.setValueAtTime(fmDepthHz, now);
          lfoOsc.connect(lfoGain);
          lfoGain.connect(primaryOsc.frequency);
          if (harm2Osc) lfoGain.connect(harm2Osc.frequency);
          if (harm3Osc) lfoGain.connect(harm3Osc.frequency);
          if (harm4Osc) lfoGain.connect(harm4Osc.frequency);
        } else if (lfoTarget() === 'am') {
          const amDepth = (lfoDepth() / 100) * 0.4;
          lfoGain.gain.setValueAtTime(amDepth, now);
          lfoOsc.connect(lfoGain);
          lfoGain.connect(masterGain.gain);
        } else if (lfoTarget() === 'filter' && filterEnabled()) {
          const filterModHz = (lfoDepth() / 100) * 1500;
          lfoGain.gain.setValueAtTime(filterModHz, now);
          lfoOsc.connect(lfoGain);
          lfoGain.connect(filterNode.frequency);
        }

        lfoOsc.start(now);
      }
    }

    setIsPlaying(true);
    startVisualizerLoop();
  }

  // Stop active oscillator nodes
  function stopActiveSoundNodes() {
    try {
      if (primaryOsc) { primaryOsc.stop(); primaryOsc.disconnect(); primaryOsc = null; }
      if (harm1GainNode) { harm1GainNode.disconnect(); harm1GainNode = null; }
      if (harm2Osc) { harm2Osc.stop(); harm2Osc.disconnect(); harm2Osc = null; }
      if (harm2GainNode) { harm2GainNode.disconnect(); harm2GainNode = null; }
      if (harm3Osc) { harm3Osc.stop(); harm3Osc.disconnect(); harm3Osc = null; }
      if (harm3GainNode) { harm3GainNode.disconnect(); harm3GainNode = null; }
      if (harm4Osc) { harm4Osc.stop(); harm4Osc.disconnect(); harm4Osc = null; }
      if (harm4GainNode) { harm4GainNode.disconnect(); harm4GainNode = null; }
      if (lfoOsc) { lfoOsc.stop(); lfoOsc.disconnect(); lfoOsc = null; }
      if (lfoGain) { lfoGain.disconnect(); lfoGain = null; }
      if (binLeftOsc) { binLeftOsc.stop(); binLeftOsc.disconnect(); binLeftOsc = null; }
      if (binLeftPan) { binLeftPan.disconnect(); binLeftPan = null; }
      if (binRightOsc) { binRightOsc.stop(); binRightOsc.disconnect(); binRightOsc = null; }
      if (binRightPan) { binRightPan.disconnect(); binRightPan = null; }
    } catch { /* tolerate */ }
  }

  function stopSound() {
    stopActiveSoundNodes();
    setIsPlaying(false);
    setBinauralActive(false);
    setSweepActive(false);
    drawIdleVisualizer();
  }

  function togglePower() {
    if (isPlaying() || binauralActive()) {
      stopSound();
    } else {
      startSound();
    }
  }

  // Update Frequency
  function handleFreqChange(newFreq) {
    const f = Math.max(MIN_FREQ, Math.min(MAX_FREQ, Math.round(newFreq * 10) / 10));
    setFrequency(f);
    setSliderVal(freqToSlider(f));

    if (audioCtx && isPlaying() && primaryOsc && primaryOsc.frequency) {
      const now = audioCtx.currentTime;
      primaryOsc.frequency.setTargetAtTime(f, now, 0.02);
      if (harm2Osc) harm2Osc.frequency.setTargetAtTime(f * 2, now, 0.02);
      if (harm3Osc) harm3Osc.frequency.setTargetAtTime(f * 3, now, 0.02);
      if (harm4Osc) harm4Osc.frequency.setTargetAtTime(f * 4, now, 0.02);
    }
  }

  function handleSliderInput(e) {
    const val = parseFloat(e.target.value);
    setSliderVal(val);
    const f = sliderToFreq(val);
    handleFreqChange(f);
  }

  function adjustFreq(delta) {
    handleFreqChange(frequency() + delta);
  }

  // Volume & Mute
  function handleVolChange(e) {
    const v = parseInt(e.target.value, 10);
    setVolume(v);
    if (audioCtx && masterGain) {
      const targetVol = isMuted() ? 0 : (v / 100) * 0.45;
      masterGain.gain.setTargetAtTime(targetVol, audioCtx.currentTime, 0.03);
    }
  }

  function toggleMute() {
    const m = !isMuted();
    setIsMuted(m);
    if (audioCtx && masterGain) {
      const targetVol = m ? 0 : (volume() / 100) * 0.45;
      masterGain.gain.setTargetAtTime(targetVol, audioCtx.currentTime, 0.03);
    }
  }

  // Select Waveform
  function chooseWaveform(w) {
    setWaveform(w);
    if (isPlaying()) {
      startSound(); // Restart with new wave architecture
    }
  }

  // Binaural Beat generator
  function startBinaural(baseHz, deltaHz) {
    initAudio();
    if (audioCtx.state === 'suspended') audioCtx.resume();
    stopActiveSoundNodes();

    setBinauralBaseHz(baseHz);
    setBinauralBeatHz(deltaHz);
    setBinauralActive(true);
    setIsPlaying(false);

    const now = audioCtx.currentTime;

    // Left Ear
    binLeftOsc = audioCtx.createOscillator();
    binLeftOsc.type = 'sine';
    binLeftOsc.frequency.setValueAtTime(baseHz, now);
    binLeftPan = audioCtx.createStereoPanner();
    binLeftPan.pan.setValueAtTime(-1.0, now);
    binLeftOsc.connect(binLeftPan);
    binLeftPan.connect(masterGain);
    binLeftOsc.start(now);

    // Right Ear
    binRightOsc = audioCtx.createOscillator();
    binRightOsc.type = 'sine';
    binRightOsc.frequency.setValueAtTime(baseHz + deltaHz, now);
    binRightPan = audioCtx.createStereoPanner();
    binRightPan.pan.setValueAtTime(1.0, now);
    binRightOsc.connect(binRightPan);
    binRightPan.connect(masterGain);
    binRightOsc.start(now);

    toast.info(`Binaural active: ${baseHz}Hz / ${baseHz + deltaHz}Hz (Δ ${deltaHz}Hz)`);
  }

  // DTMF Phone Dialing
  function triggerDtmfTone(rowHz, colHz) {
    initAudio();
    if (audioCtx.state === 'suspended') audioCtx.resume();

    stopDtmfTone();
    const now = audioCtx.currentTime;

    dtmfRowOsc = audioCtx.createOscillator();
    dtmfRowOsc.type = 'sine';
    dtmfRowOsc.frequency.setValueAtTime(rowHz, now);

    dtmfColOsc = audioCtx.createOscillator();
    dtmfColOsc.type = 'sine';
    dtmfColOsc.frequency.setValueAtTime(colHz, now);

    dtmfGain = audioCtx.createGain();
    dtmfGain.gain.setValueAtTime(0.25, now);

    dtmfRowOsc.connect(dtmfGain);
    dtmfColOsc.connect(dtmfGain);
    dtmfGain.connect(masterGain);

    dtmfRowOsc.start(now);
    dtmfColOsc.start(now);
  }

  function stopDtmfTone() {
    try {
      if (dtmfRowOsc) { dtmfRowOsc.stop(); dtmfRowOsc.disconnect(); dtmfRowOsc = null; }
      if (dtmfColOsc) { dtmfColOsc.stop(); dtmfColOsc.disconnect(); dtmfColOsc = null; }
      if (dtmfGain) { dtmfGain.disconnect(); dtmfGain = null; }
    } catch { /* tolerate */ }
  }

  // Automated Sweep Chirp
  function startSweep() {
    initAudio();
    if (audioCtx.state === 'suspended') audioCtx.resume();
    stopSound();

    setSweepActive(true);
    const now = audioCtx.currentTime;
    const dur = sweepDurationSec();
    const startHz = sweepStartHz();
    const endHz = sweepEndHz();

    primaryOsc = audioCtx.createOscillator();
    primaryOsc.type = waveform().includes('noise') ? 'sine' : waveform();
    primaryOsc.frequency.setValueAtTime(startHz, now);
    primaryOsc.frequency.exponentialRampToValueAtTime(endHz, now + dur);

    primaryOsc.connect(filterEnabled() ? filterNode : pannerNode);
    primaryOsc.start(now);
    primaryOsc.stop(now + dur);
    setIsPlaying(true);

    primaryOsc.onended = () => {
      setSweepActive(false);
      setIsPlaying(false);
      primaryOsc = null;
      toast.success('Sweep completed');
    };
  }

  // Export 2-second WAV clip
  async function exportWavTone() {
    try {
      toast.info('Rendering 2s WAV tone…');
      const sampleRate = 44100;
      const duration = 2.0;
      const numFrames = sampleRate * duration;
      const offlineCtx = new (window.OfflineAudioContext || window.webkitOfflineAudioContext)(2, numFrames, sampleRate);

      const osc = offlineCtx.createOscillator();
      osc.type = waveform().includes('noise') ? 'sine' : waveform();
      osc.frequency.setValueAtTime(frequency(), 0);

      const gain = offlineCtx.createGain();
      gain.gain.setValueAtTime(0.35, 0);

      osc.connect(gain);
      gain.connect(offlineCtx.destination);
      osc.start(0);
      osc.stop(duration);

      const renderedBuffer = await offlineCtx.startRendering();
      const wavBlob = audioBufferToWavBlob(renderedBuffer);
      const url = URL.createObjectURL(wavBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `redfur_tone_${Math.round(frequency())}Hz_${waveform()}.wav`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('WAV file downloaded!');
    } catch (err) {
      toast.error(`WAV export failed: ${err.message}`);
    }
  }

  // WAV Encoder Helper
  function audioBufferToWavBlob(buffer) {
    const numChannels = buffer.numberOfChannels;
    const sampleRate = buffer.sampleRate;
    const format = 1; // PCM
    const bitDepth = 16;
    const bytesPerSample = bitDepth / 8;
    const blockAlign = numChannels * bytesPerSample;
    const numSamples = buffer.length;
    const byteRate = sampleRate * blockAlign;
    const dataSize = numSamples * blockAlign;
    const headerSize = 44;
    const totalSize = headerSize + dataSize;
    const arrayBuffer = new ArrayBuffer(totalSize);
    const view = new DataView(arrayBuffer);

    function writeString(offset, string) {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    }

    writeString(0, 'RIFF');
    view.setUint32(4, 36 + dataSize, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, format, true);
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, byteRate, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bitDepth, true);
    writeString(36, 'data');
    view.setUint32(40, dataSize, true);

    const channels = [];
    for (let i = 0; i < numChannels; i++) {
      channels.push(buffer.getChannelData(i));
    }

    let offset = 44;
    for (let i = 0; i < numSamples; i++) {
      for (let ch = 0; ch < numChannels; ch++) {
        let sample = Math.max(-1, Math.min(1, channels[ch][i]));
        sample = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
        view.setInt16(offset, sample, true);
        offset += 2;
      }
    }
    return new Blob([view], { type: 'audio/wav' });
  }

  function drawIdleVisualizer() {
    if (!canvasRef) return;
    const canvas = canvasRef;
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    ctx.fillStyle = '#06090e';
    ctx.fillRect(0, 0, width, height);
    ctx.strokeStyle = 'rgba(255, 200, 87, 0.08)';
    ctx.lineWidth = 1;
    for (let x = 0; x < width; x += 40) {
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, height); ctx.stroke();
    }
    for (let y = 0; y < height; y += 25) {
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(width, y); ctx.stroke();
    }
    ctx.lineWidth = 1.5;
    ctx.strokeStyle = 'rgba(100, 116, 139, 0.45)';
    ctx.beginPath();
    ctx.moveTo(0, height / 2);
    ctx.lineTo(width, height / 2);
    ctx.stroke();
  }

  // Visualizer Animation Loop — only runs while audio is actively playing and visible
  function startVisualizerLoop() {
    if (animId) return;

    function render() {
      if ((!isPlaying() && !binauralActive() && !sweepActive()) || props.widget?.minimized || document.hidden) {
        animId = null;
        drawIdleVisualizer();
        return;
      }
      animId = requestAnimationFrame(render);
      if (!canvasRef || !analyser) return;

      const canvas = canvasRef;
      const ctx = canvas.getContext('2d');
      const width = canvas.width;
      const height = canvas.height;

      // Dark background with phosphor grid
      ctx.fillStyle = '#06090e';
      ctx.fillRect(0, 0, width, height);

      // Subtle phosphor grid
      ctx.strokeStyle = 'rgba(255, 200, 87, 0.08)';
      ctx.lineWidth = 1;
      for (let x = 0; x < width; x += 40) {
        ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, height); ctx.stroke();
      }
      for (let y = 0; y < height; y += 25) {
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(width, y); ctx.stroke();
      }

      if (vizMode() === 'oscilloscope') {
        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);
        analyser.getByteTimeDomainData(dataArray);

        ctx.lineWidth = 2;
        ctx.strokeStyle = isPlaying() || binauralActive() ? '#4ade80' : 'rgba(100, 116, 139, 0.5)';

        ctx.beginPath();
        const sliceWidth = width / bufferLength;
        let x = 0;
        for (let i = 0; i < bufferLength; i++) {
          const v = dataArray[i] / 128.0;
          const y = (v * height) / 2;
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
          x += sliceWidth;
        }
        ctx.stroke();
      } else {
        // Spectrum FFT Bars
        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);
        analyser.getByteFrequencyData(dataArray);

        const barWidth = (width / bufferLength) * 2.5;
        let x = 0;
        for (let i = 0; i < bufferLength; i++) {
          const barHeight = (dataArray[i] / 255) * height;
          const grad = ctx.createLinearGradient(0, height, 0, height - barHeight);
          grad.addColorStop(0, '#ffc857');
          grad.addColorStop(0.7, '#38bdf8');
          grad.addColorStop(1, '#f87171');
          ctx.fillStyle = grad;
          ctx.fillRect(x, height - barHeight, barWidth - 1, barHeight);
          x += barWidth;
          if (x > width) break;
        }
      }
    }
    render();
  }

  onMount(() => {
    if (canvasRef) {
      canvasRef.width = canvasRef.offsetWidth || 560;
      canvasRef.height = canvasRef.offsetHeight || 120;
      drawIdleVisualizer();
    }
  });

  onCleanup(() => {
    if (animId) cancelAnimationFrame(animId);
    stopSound();
    stopDtmfTone();
    if (audioCtx && audioCtx.state !== 'closed') {
      audioCtx.close().catch(() => {});
    }
  });

  const presetList = createMemo(() => data()?.presets || [
    { name: 'Sub-bass 40 Hz', freq: 40, wave: 'sine', category: 'bass' },
    { name: 'Sub-bass 60 Hz', freq: 60, wave: 'sine', category: 'bass' },
    { name: 'Mains Hum 60 Hz', freq: 60, wave: 'sawtooth', category: 'utility' },
    { name: 'Tuning A4 (440 Hz)', freq: 440, wave: 'sine', category: 'tuning' },
    { name: 'Treble C5 (523 Hz)', freq: 523.25, wave: 'triangle', category: 'tuning' },
    { name: 'Calibration 1 kHz', freq: 1000, wave: 'sine', category: 'test' },
    { name: 'High Treble 10 kHz', freq: 10000, wave: 'sine', category: 'test' },
  ]);

  const binauralBands = createMemo(() => data()?.binauralBands || [
    { name: 'Delta', min: 0.5, max: 4, desc: 'Deep Sleep & Healing', defaultHz: 2 },
    { name: 'Theta', min: 4, max: 8, desc: 'Deep Meditation & REM', defaultHz: 6 },
    { name: 'Alpha', min: 8, max: 13, desc: 'Relaxation & Flow', defaultHz: 10 },
    { name: 'Beta', min: 13, max: 30, desc: 'Active Focus & Alertness', defaultHz: 20 },
    { name: 'Gamma', min: 30, max: 50, desc: 'Peak Cognition', defaultHz: 40 },
  ]);

  const dtmfKeys = [
    { label: '1', r: 697, c: 1209 }, { label: '2', r: 697, c: 1336 }, { label: '3', r: 697, c: 1477 },
    { label: '4', r: 770, c: 1209 }, { label: '5', r: 770, c: 1336 }, { label: '6', r: 770, c: 1477 },
    { label: '7', r: 852, c: 1209 }, { label: '8', r: 852, c: 1336 }, { label: '9', r: 852, c: 1477 },
    { label: '*', r: 941, c: 1209 }, { label: '0', r: 941, c: 1336 }, { label: '#', r: 941, c: 1477 },
  ];

  return (
    <div class="v2-widget tonegen-root">
      {/* Widget Header */}
      <div class="v2-widget-hdr" style={{ display: 'flex', 'align-items': 'center' }}>
        <span class="v2-widget-hdr__title">🎛️ Audio Lab & Tone Synthesizer</span>
        <div style={{ 'margin-left': 'auto', display: 'flex', gap: '6px' }}>
          <button
            type="button"
            class="v2-widget-hdr__btn"
            title="Export WAV"
            onClick={exportWavTone}
          >💾 WAV</button>
          <button
            type="button"
            class="v2-widget-hdr__btn"
            title="Refresh Preset Data"
            onClick={() => refetch()}
          >↻</button>
        </div>
      </div>

      <div class="tonegen-scroll">

        {/* Master Power Bar & Volume */}
        <div class="tonegen-powerbar">
          <button
            type="button"
            class={`tonegen-btn-power ${isPlaying() || binauralActive() ? 'is-active' : ''}`}
            onClick={togglePower}
          >
            <span class="tonegen-power-led" />
            <span>{isPlaying() || binauralActive() ? 'Tone Active' : 'Start Tone'}</span>
          </button>

          <div class="tonegen-master-gain">
            <button
              type="button"
              class="tonegen-step-btn"
              style={{ color: isMuted() ? 'var(--bad, #f87171)' : 'inherit' }}
              onClick={toggleMute}
            >
              {isMuted() ? '🔇 Muted' : '🔊 Gain'}
            </button>
            <input
              type="range"
              class="tonegen-vol-slider"
              min="0"
              max="100"
              value={volume()}
              onInput={handleVolChange}
            />
            <span class="tonegen-vol-badge">{isMuted() ? '0%' : `${volume()}%`}</span>
          </div>
        </div>

        {/* Live Visualizer Display */}
        <div class="tonegen-viz-card">
          <canvas ref={canvasRef} class="tonegen-canvas" />
          <div class="tonegen-viz-controls">
            <button
              type="button"
              class={`tonegen-viz-mode-btn ${vizMode() === 'oscilloscope' ? 'is-active' : ''}`}
              onClick={() => setVizMode('oscilloscope')}
            >SCOPE</button>
            <button
              type="button"
              class={`tonegen-viz-mode-btn ${vizMode() === 'spectrum' ? 'is-active' : ''}`}
              onClick={() => setVizMode('spectrum')}
            >FFT</button>
          </div>
          <div class="tonegen-viz-status">
            {isPlaying() ? `SIGNAL: ${frequency()} Hz (${waveform()})` : binauralActive() ? `BINAURAL: ${binauralBaseHz()} Hz` : 'IDLE'}
          </div>
        </div>

        {/* Primary Frequency Controller */}
        <div class="tonegen-card">
          <div class="tonegen-card-hdr">
            <span class="tonegen-card-title">Frequency & Pitch</span>
            <span class="tonegen-note-badge">{freqToNote(frequency())}</span>
          </div>

          <div class="tonegen-freq-display">
            <div>
              <span class="tonegen-freq-num">{frequency()}</span>
              <span class="tonegen-freq-unit">Hz</span>
            </div>
            <div class="tonegen-stepper-row">
              <button type="button" class="tonegen-step-btn" onClick={() => adjustFreq(-100)}>-100</button>
              <button type="button" class="tonegen-step-btn" onClick={() => adjustFreq(-10)}>-10</button>
              <button type="button" class="tonegen-step-btn" onClick={() => adjustFreq(-1)}>-1</button>
              <button type="button" class="tonegen-step-btn" onClick={() => adjustFreq(1)}>+1</button>
              <button type="button" class="tonegen-step-btn" onClick={() => adjustFreq(10)}>+10</button>
              <button type="button" class="tonegen-step-btn" onClick={() => adjustFreq(100)}>+100</button>
            </div>
          </div>

          {/* Logarithmic Frequency Slider */}
          <div class="tonegen-slider-row">
            <input
              type="range"
              class="tonegen-slider"
              min="0"
              max="1000"
              step="1"
              value={sliderVal()}
              onInput={handleSliderInput}
            />
            <div style={{ display: 'flex', 'justify-content': 'space-between', 'font-size': '10px', color: 'var(--t-dim)' }}>
              <span>20 Hz (Sub)</span>
              <span>250 Hz</span>
              <span>1 kHz</span>
              <span>5 kHz</span>
              <span>20 kHz (Limit)</span>
            </div>
          </div>

          {/* Waveform Selector */}
          <div class="tonegen-wave-selector">
            <button
              type="button"
              class={`tonegen-wave-btn ${waveform() === 'sine' ? 'is-active' : ''}`}
              onClick={() => chooseWaveform('sine')}
            >
              <span>∿ Sine</span>
              <span style={{ 'font-size': '9px', opacity: '0.7' }}>Pure Tone</span>
            </button>
            <button
              type="button"
              class={`tonegen-wave-btn ${waveform() === 'triangle' ? 'is-active' : ''}`}
              onClick={() => chooseWaveform('triangle')}
            >
              <span>▲ Triangle</span>
              <span style={{ 'font-size': '9px', opacity: '0.7' }}>Soft Rich</span>
            </button>
            <button
              type="button"
              class={`tonegen-wave-btn ${waveform() === 'square' ? 'is-active' : ''}`}
              onClick={() => chooseWaveform('square')}
            >
              <span>⊓ Square</span>
              <span style={{ 'font-size': '9px', opacity: '0.7' }}>Hollow Odd</span>
            </button>
            <button
              type="button"
              class={`tonegen-wave-btn ${waveform() === 'sawtooth' ? 'is-active' : ''}`}
              onClick={() => chooseWaveform('sawtooth')}
            >
              <span>⊿ Sawtooth</span>
              <span style={{ 'font-size': '9px', opacity: '0.7' }}>Buzz All</span>
            </button>
            <button
              type="button"
              class={`tonegen-wave-btn ${waveform() === 'white-noise' ? 'is-active' : ''}`}
              onClick={() => chooseWaveform('white-noise')}
            >
              <span>░ White Noise</span>
              <span style={{ 'font-size': '9px', opacity: '0.7' }}>Flat Energy</span>
            </button>
            <button
              type="button"
              class={`tonegen-wave-btn ${waveform() === 'pink-noise' ? 'is-active' : ''}`}
              onClick={() => chooseWaveform('pink-noise')}
            >
              <span>▒ Pink Noise</span>
              <span style={{ 'font-size': '9px', opacity: '0.7' }}>1/f Octave</span>
            </button>
          </div>
        </div>

        {/* Tab Navigation for Extended Audio Tools */}
        <div class="tonegen-tabs-hdr">
          <button
            type="button"
            class={`tonegen-tab-btn ${activeTab() === 'presets' ? 'is-active' : ''}`}
            onClick={() => setActiveTab('presets')}
          >📌 Presets</button>
          <button
            type="button"
            class={`tonegen-tab-btn ${activeTab() === 'modulation' ? 'is-active' : ''}`}
            onClick={() => setActiveTab('modulation')}
          >〰️ Modulation (LFO)</button>
          <button
            type="button"
            class={`tonegen-tab-btn ${activeTab() === 'harmonics' ? 'is-active' : ''}`}
            onClick={() => setActiveTab('harmonics')}
          >🎚️ Harmonics</button>
          <button
            type="button"
            class={`tonegen-tab-btn ${activeTab() === 'filter' ? 'is-active' : ''}`}
            onClick={() => setActiveTab('filter')}
          >🎛️ Filter</button>
          <button
            type="button"
            class={`tonegen-tab-btn ${activeTab() === 'binaural' ? 'is-active' : ''}`}
            onClick={() => setActiveTab('binaural')}
          >🎧 Binaural</button>
          <button
            type="button"
            class={`tonegen-tab-btn ${activeTab() === 'dtmf' ? 'is-active' : ''}`}
            onClick={() => setActiveTab('dtmf')}
          >📞 DTMF Dial</button>
          <button
            type="button"
            class={`tonegen-tab-btn ${activeTab() === 'sweep' ? 'is-active' : ''}`}
            onClick={() => setActiveTab('sweep')}
          >📈 Chirp Sweep</button>
        </div>

        {/* Tab 1: Presets */}
        <Show when={activeTab() === 'presets'}>
          <div class="tonegen-card">
            <span class="tonegen-card-title">Quick Calibration Presets</span>
            <div class="tonegen-presets-grid">
              <For each={presetList()}>
                {(p) => (
                  <button
                    type="button"
                    class="tonegen-preset-chip"
                    onClick={() => {
                      chooseWaveform(p.wave);
                      handleFreqChange(p.freq);
                      if (!isPlaying()) startSound();
                    }}
                  >
                    <span>{p.name}</span>
                    <span class="tonegen-preset-chip-freq">{p.freq} Hz</span>
                  </button>
                )}
              </For>
            </div>
          </div>
        </Show>

        {/* Tab 2: LFO Modulation */}
        <Show when={activeTab() === 'modulation'}>
          <div class="tonegen-card">
            <div class="tonegen-card-hdr">
              <span class="tonegen-card-title">LFO Modulation Engine</span>
              <button
                type="button"
                class="tonegen-step-btn"
                style={{ color: lfoEnabled() ? '#4ade80' : 'inherit' }}
                onClick={() => {
                  setLfoEnabled(!lfoEnabled());
                  if (isPlaying()) startSound();
                }}
              >
                {lfoEnabled() ? '● LFO ON' : '○ LFO BYPASS'}
              </button>
            </div>

            <div class="tonegen-grid-2col">
              {/* Target & Waveform */}
              <div class="tonegen-ctrl-group">
                <div class="tonegen-ctrl-label-row">
                  <span class="tonegen-ctrl-name">Target Mode</span>
                  <span class="tonegen-ctrl-val">{lfoTarget().toUpperCase()}</span>
                </div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button
                    type="button"
                    class={`tonegen-step-btn ${lfoTarget() === 'fm' ? 'tonegen-viz-mode-btn is-active' : ''}`}
                    onClick={() => { setLfoTarget('fm'); if (isPlaying()) startSound(); }}
                  >FM (Pitch)</button>
                  <button
                    type="button"
                    class={`tonegen-step-btn ${lfoTarget() === 'am' ? 'tonegen-viz-mode-btn is-active' : ''}`}
                    onClick={() => { setLfoTarget('am'); if (isPlaying()) startSound(); }}
                  >AM (Tremolo)</button>
                  <button
                    type="button"
                    class={`tonegen-step-btn ${lfoTarget() === 'filter' ? 'tonegen-viz-mode-btn is-active' : ''}`}
                    onClick={() => { setLfoTarget('filter'); if (isPlaying()) startSound(); }}
                  >Filter (Wah)</button>
                </div>
              </div>

              {/* LFO Waveform */}
              <div class="tonegen-ctrl-group">
                <div class="tonegen-ctrl-label-row">
                  <span class="tonegen-ctrl-name">LFO Waveform</span>
                  <span class="tonegen-ctrl-val">{lfoWave()}</span>
                </div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button type="button" class="tonegen-step-btn" onClick={() => { setLfoWave('sine'); if (isPlaying()) startSound(); }}>Sine</button>
                  <button type="button" class="tonegen-step-btn" onClick={() => { setLfoWave('triangle'); if (isPlaying()) startSound(); }}>Tri</button>
                  <button type="button" class="tonegen-step-btn" onClick={() => { setLfoWave('square'); if (isPlaying()) startSound(); }}>Square</button>
                  <button type="button" class="tonegen-step-btn" onClick={() => { setLfoWave('sawtooth'); if (isPlaying()) startSound(); }}>Saw</button>
                </div>
              </div>
            </div>

            {/* LFO Rate & Depth Sliders */}
            <div class="tonegen-grid-2col">
              <div class="tonegen-ctrl-group">
                <div class="tonegen-ctrl-label-row">
                  <span class="tonegen-ctrl-name">LFO Speed (Rate)</span>
                  <span class="tonegen-ctrl-val">{lfoRate()} Hz</span>
                </div>
                <input
                  type="range"
                  class="tonegen-slider"
                  min="0.1"
                  max="40"
                  step="0.1"
                  value={lfoRate()}
                  onInput={(e) => {
                    const r = parseFloat(e.target.value);
                    setLfoRate(r);
                    if (lfoOsc && audioCtx) lfoOsc.frequency.setValueAtTime(r, audioCtx.currentTime);
                  }}
                />
              </div>

              <div class="tonegen-ctrl-group">
                <div class="tonegen-ctrl-label-row">
                  <span class="tonegen-ctrl-name">Modulation Depth</span>
                  <span class="tonegen-ctrl-val">{lfoDepth()}%</span>
                </div>
                <input
                  type="range"
                  class="tonegen-slider"
                  min="0"
                  max="100"
                  value={lfoDepth()}
                  onInput={(e) => {
                    const d = parseInt(e.target.value, 10);
                    setLfoDepth(d);
                    if (lfoGain && audioCtx) {
                      const val = lfoTarget() === 'fm' ? (d / 100) * 500 : lfoTarget() === 'am' ? (d / 100) * 0.4 : (d / 100) * 1500;
                      lfoGain.gain.setTargetAtTime(val, audioCtx.currentTime, 0.02);
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </Show>

        {/* Tab 3: Harmonics Additive Mixer */}
        <Show when={activeTab() === 'harmonics'}>
          <div class="tonegen-card">
            <span class="tonegen-card-title">Additive Harmonic Overtone Mixer</span>
            <div class="tonegen-harmonics-grid">
              <div class="tonegen-harmonic-col">
                <span class="tonegen-harmonic-label">1x Base</span>
                <input
                  type="range"
                  class="tonegen-harmonic-slider"
                  min="0"
                  max="100"
                  value={harm1Gain()}
                  onInput={(e) => {
                    const g = parseInt(e.target.value, 10);
                    setHarm1Gain(g);
                    if (harm1GainNode && audioCtx) harm1GainNode.gain.setTargetAtTime(g / 100, audioCtx.currentTime, 0.02);
                  }}
                />
                <span class="tonegen-harmonic-val">{harm1Gain()}%</span>
              </div>

              <div class="tonegen-harmonic-col">
                <span class="tonegen-harmonic-label">2x Octave</span>
                <input
                  type="range"
                  class="tonegen-harmonic-slider"
                  min="0"
                  max="100"
                  value={harm2Gain()}
                  onInput={(e) => {
                    const g = parseInt(e.target.value, 10);
                    setHarm2Gain(g);
                    if (harm2GainNode && audioCtx) harm2GainNode.gain.setTargetAtTime(g / 100, audioCtx.currentTime, 0.02);
                  }}
                />
                <span class="tonegen-harmonic-val">{harm2Gain()}%</span>
              </div>

              <div class="tonegen-harmonic-col">
                <span class="tonegen-harmonic-label">3x 5th</span>
                <input
                  type="range"
                  class="tonegen-harmonic-slider"
                  min="0"
                  max="100"
                  value={harm3Gain()}
                  onInput={(e) => {
                    const g = parseInt(e.target.value, 10);
                    setHarm3Gain(g);
                    if (harm3GainNode && audioCtx) harm3GainNode.gain.setTargetAtTime(g / 100, audioCtx.currentTime, 0.02);
                  }}
                />
                <span class="tonegen-harmonic-val">{harm3Gain()}%</span>
              </div>

              <div class="tonegen-harmonic-col">
                <span class="tonegen-harmonic-label">4x 2-Oct</span>
                <input
                  type="range"
                  class="tonegen-harmonic-slider"
                  min="0"
                  max="100"
                  value={harm4Gain()}
                  onInput={(e) => {
                    const g = parseInt(e.target.value, 10);
                    setHarm4Gain(g);
                    if (harm4GainNode && audioCtx) harm4GainNode.gain.setTargetAtTime(g / 100, audioCtx.currentTime, 0.02);
                  }}
                />
                <span class="tonegen-harmonic-val">{harm4Gain()}%</span>
              </div>
            </div>
          </div>
        </Show>

        {/* Tab 4: Biquad Filter */}
        <Show when={activeTab() === 'filter'}>
          <div class="tonegen-card">
            <div class="tonegen-card-hdr">
              <span class="tonegen-card-title">Biquad Filter Shaper</span>
              <button
                type="button"
                class="tonegen-step-btn"
                style={{ color: filterEnabled() ? '#4ade80' : 'inherit' }}
                onClick={() => {
                  setFilterEnabled(!filterEnabled());
                  if (isPlaying()) startSound();
                }}
              >
                {filterEnabled() ? '● FILTER ACTIVE' : '○ FILTER BYPASS'}
              </button>
            </div>

            <div style={{ display: 'flex', gap: '6px' }}>
              <button
                type="button"
                class={`tonegen-step-btn ${filterType() === 'lowpass' ? 'tonegen-viz-mode-btn is-active' : ''}`}
                onClick={() => { setFilterType('lowpass'); if (filterNode) filterNode.type = 'lowpass'; }}
              >Lowpass</button>
              <button
                type="button"
                class={`tonegen-step-btn ${filterType() === 'highpass' ? 'tonegen-viz-mode-btn is-active' : ''}`}
                onClick={() => { setFilterType('highpass'); if (filterNode) filterNode.type = 'highpass'; }}
              >Highpass</button>
              <button
                type="button"
                class={`tonegen-step-btn ${filterType() === 'bandpass' ? 'tonegen-viz-mode-btn is-active' : ''}`}
                onClick={() => { setFilterType('bandpass'); if (filterNode) filterNode.type = 'bandpass'; }}
              >Bandpass</button>
              <button
                type="button"
                class={`tonegen-step-btn ${filterType() === 'notch' ? 'tonegen-viz-mode-btn is-active' : ''}`}
                onClick={() => { setFilterType('notch'); if (filterNode) filterNode.type = 'notch'; }}
              >Notch</button>
            </div>

            <div class="tonegen-ctrl-group">
              <div class="tonegen-ctrl-label-row">
                <span class="tonegen-ctrl-name">Cutoff Frequency</span>
                <span class="tonegen-ctrl-val">{filterCutoff()} Hz</span>
              </div>
              <input
                type="range"
                class="tonegen-slider"
                min="20"
                max="18000"
                step="10"
                value={filterCutoff()}
                onInput={(e) => {
                  const c = parseInt(e.target.value, 10);
                  setFilterCutoff(c);
                  if (filterNode && audioCtx) filterNode.frequency.setValueAtTime(c, audioCtx.currentTime);
                }}
              />
            </div>

            <div class="tonegen-ctrl-group">
              <div class="tonegen-ctrl-label-row">
                <span class="tonegen-ctrl-name">Resonance (Q)</span>
                <span class="tonegen-ctrl-val">{filterQ()}</span>
              </div>
              <input
                type="range"
                class="tonegen-slider"
                min="0.1"
                max="18"
                step="0.1"
                value={filterQ()}
                onInput={(e) => {
                  const q = parseFloat(e.target.value);
                  setFilterQ(q);
                  if (filterNode && audioCtx) filterNode.Q.setValueAtTime(q, audioCtx.currentTime);
                }}
              />
            </div>
          </div>
        </Show>

        {/* Tab 5: Binaural Beats */}
        <Show when={activeTab() === 'binaural'}>
          <div class="tonegen-card">
            <div class="tonegen-card-hdr">
              <span class="tonegen-card-title">Binaural Brainwave Entrainment</span>
              <span style={{ 'font-size': '11px', color: '#38bdf8' }}>Requires Stereo Headphones</span>
            </div>

            <div style={{ display: 'flex', 'flex-direction': 'column', gap: '6px' }}>
              <For each={binauralBands()}>
                {(band) => (
                  <div class="tonegen-binaural-row">
                    <div>
                      <strong style={{ color: 'var(--fissal)' }}>{band.name}</strong>
                      <span style={{ 'font-size': '11px', color: 'var(--t-dim)', 'margin-left': '8px' }}>
                        ({band.min} - {band.max} Hz) · {band.desc}
                      </span>
                    </div>
                    <button
                      type="button"
                      class="tonegen-step-btn"
                      onClick={() => startBinaural(binauralBaseHz(), band.defaultHz)}
                    >
                      ▶ Play {band.defaultHz}Hz Δ
                    </button>
                  </div>
                )}
              </For>
            </div>
          </div>
        </Show>

        {/* Tab 6: DTMF Phone Keypad */}
        <Show when={activeTab() === 'dtmf'}>
          <div class="tonegen-card">
            <span class="tonegen-card-title" style={{ 'text-align': 'center' }}>Telephone DTMF Dial Pad</span>
            <div class="tonegen-dtmf-grid">
              <For each={dtmfKeys}>
                {(key) => (
                  <button
                    type="button"
                    class="tonegen-dtmf-key"
                    onMouseDown={() => triggerDtmfTone(key.r, key.c)}
                    onMouseUp={stopDtmfTone}
                    onMouseLeave={stopDtmfTone}
                    onTouchStart={() => triggerDtmfTone(key.r, key.c)}
                    onTouchEnd={stopDtmfTone}
                  >
                    <span>{key.label}</span>
                    <span class="tonegen-dtmf-sub">{key.r}/{key.c}Hz</span>
                  </button>
                )}
              </For>
            </div>
          </div>
        </Show>

        {/* Tab 7: Sweep Chirp */}
        <Show when={activeTab() === 'sweep'}>
          <div class="tonegen-card">
            <span class="tonegen-card-title">Chirp Frequency Sweep Generator</span>
            <div class="tonegen-sweep-row">
              <div class="tonegen-ctrl-group">
                <span class="tonegen-ctrl-name">Start Freq</span>
                <input
                  type="number"
                  class="v2-input"
                  value={sweepStartHz()}
                  onInput={(e) => setSweepStartHz(parseInt(e.target.value, 10) || 100)}
                />
              </div>

              <div class="tonegen-ctrl-group">
                <span class="tonegen-ctrl-name">End Freq</span>
                <input
                  type="number"
                  class="v2-input"
                  value={sweepEndHz()}
                  onInput={(e) => setSweepEndHz(parseInt(e.target.value, 10) || 3000)}
                />
              </div>

              <div class="tonegen-ctrl-group">
                <span class="tonegen-ctrl-name">Duration (s)</span>
                <input
                  type="number"
                  class="v2-input"
                  min="1"
                  max="30"
                  value={sweepDurationSec()}
                  onInput={(e) => setSweepDurationSec(parseInt(e.target.value, 10) || 4)}
                />
              </div>

              <button
                type="button"
                class="btn"
                style={{ height: '34px', background: 'rgba(255, 200, 87, 0.2)', 'border-color': 'var(--fissal)' }}
                disabled={sweepActive()}
                onClick={startSweep}
              >
                {sweepActive() ? 'Sweeping…' : '▶ Execute Sweep'}
              </button>
            </div>
          </div>
        </Show>

      </div>
    </div>
  );
}
